import React, { Fragment, useEffect, useRef } from 'react';
import { Unity, useUnityContext } from "react-unity-webgl";
import { auth, isLoggedIn, SignOutAndClearAllData, deleteAccount, getValidIdToken } from '../firebase/firebase-handler';
import { GetVersionNum } from '../util/VersionNumber';
import { CancelButton } from '../util/CancelButton';
import { GetCurrentTimezone, GetCachedUrlParams, UpdateUrlParams, GetQualityPreference, SetQualityPreference } from '../util/util';
import { ModalInputWrapper, openInputField, forceCloseInputField } from '../modal/ModalInput';
import { openStripePurchaseModal, ModalPurchaseStripe } from "../modal/ModalPurchaseStripe";
import { openPayPalPurchaseModal, ModalPurchasePayPal } from '../modal/ModalPurchasePayPal';

const GamePage = () => {
    const initializedRef = useRef(false);
    const unityContainerRef = useRef(null);
    const unityCanvasRef = useRef(null);
    // A ref, not state - handleDeviceMotion is registered once (via OnUnityEnableShake,
    // itself only ever wired up once, guarded by initializedRef) and never re-created, so
    // a useState value here would be permanently frozen at its value from that one render -
    // the cooldown check would always compare against 0, meaning it never actually
    // throttled anything. A ref's .current is always live regardless of closure staleness.
    const lastShakeTimestampRef = useRef(0);
    // Set once a real user gesture (any tap/click) has successfully unlocked
    // DeviceMotion permission - see the effect below.
    const motionPermissionResolvedRef = useRef(false);
    // Previous devicemotion reading + when it happened - used to measure the *change* in
    // acceleration (jerk) between readings rather than comparing a single instantaneous
    // reading against a fixed cutoff. See handleDeviceMotion for why.
    const lastAccelerationRef = useRef(null);
    const lastMotionTimestampRef = useRef(0);
    // Set true once Unity has confirmed the player actually wants to leave (via the
    // "Leave Tipsy Nites?" popup) - lets the back-trap's popstate handler stand down instead
    // of re-arming itself, so the browser's own back navigation can finally go through.
    const exitingAppRef = useRef(false);

    const { unityProvider, loadingProgression, isLoaded, sendMessage, addEventListener, removeEventListener, requestFullscreen } = useUnityContext({
        loaderUrl: "unity_build/partytrivia-web-app.loader.js",
        dataUrl: "unity_build/partytrivia-web-app.data.unityweb",
        frameworkUrl: "unity_build/partytrivia-web-app.framework.js.unityweb",
        codeUrl: "unity_build/partytrivia-web-app.wasm.unityweb",
        streamingAssetsUrl: "StreamingAssets",
        // Must match ProjectSettings.asset's companyName/productName/bundleVersion exactly -
        // Unity WebGL uses companyName+productName to name the IndexedDB store behind
        // PlayerPrefs, so a mismatch here isn't just cosmetic (loader.js console warning),
        // it'd put saved player data in a differently-named store than a native build.
        companyName: "AfroGames",
        productName: "Tipsy Nites",
        productVersion: "0.1.0",
        webglContextAttributes: {
            preserveDrawingBuffer: true, // Ensures better rendering quality
        },
        devicePixelRatio: window.devicePixelRatio, // Sets high-quality rendering
    });

    const onUnityLogOut = (json) => {
        console.log(`[onUnityLogOut] got logout request with the json: ${json}`);
        SignOutAndClearAllData();
    };

    const onUnityDeleteAccount = (json) => {
        console.log(`[onUnityDeleteAccount] got delete account request with the json: ${json}`);
        deleteAccount();
    };

    // Unity has shown its "Leave Tipsy Nites?" popup and the player confirmed - let the browser
    // Back navigation the trap below has been swallowing actually go through now. Popping just
    // once would only land back on our own trap entry (still the same document, since it was
    // pushed via pushState) - go(-2) clears both that trap entry and the real landing entry
    // underneath it in one shot. If there's nothing further back (e.g. a freshly opened tab),
    // this is a harmless no-op, same as an unintercepted Back press would've been.
    const onUnityConfirmLeaveApp = () => {
        console.log('[onUnityConfirmLeaveApp] player confirmed leaving - releasing the back-button trap');
        exitingAppRef.current = true;
        window.history.go(-2);
    };

    const onUnityUpdateCachedURLParam = (json) => {
        var config = JSON.parse(json);
        console.log(`[onUnityUpdateCachedValue] got update key[${config.key}] with value: ${config.value}`);
        UpdateUrlParams(config.key, config.value);
    };

    const onUnityCopyToClipboard = async (text) => {
        console.log(`[UnityCopyToClipboard] unity request to CopyToClipboard with the text: ${text}`);
        
        // Copy value to clipboard regardless
        try {
            await navigator.clipboard.writeText(text);
            console.log(`Copied text to clipboard: ${text}`);
            //alert(`Copied text to clipboard: ${text}`);
        } catch (error) {
            console.error(`Could not copy text: ${error}`);
        }

        // Attempt to call Web Share API
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Tipsy Nites",
                    text: text,
                });

                console.log("Shared successfully");
                return;
            } catch (err) {
                console.warn("Share failed or was cancelled:", err);
                console.log(`Copied text to clipboard: ${text}`);
                // Fall back to clipboard
            }
        }
    };

    const onUnityVibrate = (json) => {
        var value = JSON.parse(json);
        console.log(`[onUnityVibrate] unity request to vibrate with value: ${value}`);
        if (navigator.vibrate) {
            navigator.vibrate(value);
        }
    };

    const onUnityVibratePattern = (json) => {
        // json should be in for the format of = { "patternArray": int[], "length": int}
        var value = JSON.parse(json);
        var patternArray = value?.patternArray;
        var length = value?.length;
        console.log(`[onUnityVibrate] unity request to vibrate pattern with patternArray: ${patternArray}, with lenght: ${length}`);
        
        if (navigator.vibrate) {
            var pattern = Array.from({length: length}, (v, i) => module.HEAP32[patternArray / 4 + i]);
            navigator.vibrate(pattern);
        }
    };

    const OpenURL = (url) => {
        console.log(`[OpenURL] unity request to OpenURL with the url: ${url}`);
        try {
            window.open(url, "_blank", "noopener,noreferrer");
            console.log(`[OpenURL] Successfully opened: ${url}`);
        } catch (error) {
            console.error(`[OpenURL] Failed to open URL: ${error}`);
            alert("Unable to open the link.");
        }
    };

    // json is { fileName, content } - triggers a real browser file download via a Blob +
    // temporary <a download> click, since there's no other way to hand the player an actual
    // downloadable file from WebGL (Unity's own File I/O only writes into the virtual IDBFS
    // filesystem, which the player can't get to).
    const onUnityDownloadTextFile = (json) => {
        console.log(`[UnityDownloadTextFile] unity request to download a text file`);
        try {
            const { fileName, content } = JSON.parse(json);
            const blob = new Blob([content], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || "log.txt";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`[UnityDownloadTextFile] Failed to download file: ${error}`);
            alert("Unable to download the file.");
        }
    };

    const onUnityOpenInputField = (json) => {
        console.log(`[onUnityOpenInputField] unity request to open input field: ${json}`);
        const config = JSON.parse(json);
        openInputField(config);
    };

    const onUnityCloseInputField = (json) => {
        console.log(`[onUnityCloseInputField] unity request to close input field: ${json}`);
        forceCloseInputField();
    };

    // Function to handle shaking detection.
    //
    // Deliberately NOT comparing accelerationIncludingGravity's raw magnitude against a
    // fixed cutoff - that magnitude has Earth's gravity (~9.8 m/s^2) baked into every
    // reading, and different phones/browsers report slightly different baseline values at
    // rest (sensor calibration varies by manufacturer). Against a fixed threshold, a phone
    // with a naturally higher baseline needs *less* real shake motion to cross it than one
    // with a lower baseline - unfair regardless of how hard someone is actually shaking.
    //
    // Using the CHANGE since the previous reading (jerk) cancels gravity out almost
    // entirely - it barely moves frame to frame, while a real shake causes a large swing -
    // and dividing by the actual elapsed time between readings corrects for devices/
    // browsers that sample motion at different native rates, so a phone that happens to
    // report readings twice as often doesn't get twice as many chances to look "shakier".
    const handleDeviceMotion = (event) => {
        const { accelerationIncludingGravity } = event;
        if (!accelerationIncludingGravity) return;

        const { x, y, z } = accelerationIncludingGravity;
        if (x === null || y === null || z === null) return;

        const now = Date.now();
        const previous = lastAccelerationRef.current;
        lastAccelerationRef.current = { x, y, z };

        if (previous === null) {
            // First reading - nothing to compare against yet.
            lastMotionTimestampRef.current = now;
            return;
        }

        const elapsedSeconds = Math.max((now - lastMotionTimestampRef.current) / 1000, 0.001);
        lastMotionTimestampRef.current = now;

        const dx = x - previous.x;
        const dy = y - previous.y;
        const dz = z - previous.z;
        const deltaMagnitude = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const jerk = deltaMagnitude / elapsedSeconds; // rate of change (m/s^3), not a raw per-sample delta

        // Tuned against a rough estimate of what a genuine shake looks like at typical
        // sampling rates - this is a starting point, not a measured value, and is worth
        // dialing in against a couple of real devices rather than trusting as-is.
        const SHAKE_JERK_THRESHOLD = 400;
        const SHAKE_COOLDOWN = 100; // Minimum time (in ms) between shakes

        if (jerk > SHAKE_JERK_THRESHOLD && now - lastShakeTimestampRef.current > SHAKE_COOLDOWN) {
            console.log(`[handleDeviceMotion] Shake detected! jerk=${jerk.toFixed(0)}`);
            lastShakeTimestampRef.current = now;
            sendMessage("UnityToWeb", "IncreaseShakeCounter", "");
        }
    };
    
    const OnUnityEnableShake = async (json) => {
        try {
            if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
                const permission = await DeviceMotionEvent.requestPermission();
                if (permission !== "granted") {
                    console.warn("Motion event permission not granted");
                    return;
                }
            }
            console.log("[OnUnityEnableShake] Motion event permission granted or not required");
            window.addEventListener("devicemotion", handleDeviceMotion);
        } catch (error) {
            console.error("Error enabling shake detection:", error);
        }
    };
    
    const OnUnityDisableShake = (json) => {
        console.log("[OnUnityDisableShake] Removing shake event listener");
        window.removeEventListener("devicemotion", handleDeviceMotion);
    };

    const OnUnityRefreshAuthToken = async (json) => {
        try {
            const result = await getValidIdToken();
            console.log("[OnRefreshAuthToken] Got refresh token:", result);
            const data = JSON.stringify(auth.currentUser?.stsTokenManager);
            console.log("[OnRefreshAuthToken] sending data:", data);
            sendMessage('UnityToWeb', 'RefreshAuthToken', data); 

        } catch (error) {
            const authInfo = {};
            console.error("[OnRefreshAuthToken] Error requesting auth refresh:", error);
            sendMessage('UnityToWeb', 'RefreshAuthToken', JSON.stringify(authInfo)); 
        }
        
    };

    const onUnityRequestPlayerInfo = (json) => {
        if (isLoggedIn()) {
            const playerInfo = { 
                //firebase data 
                "id" : auth.currentUser.uid,
                "data" : auth.currentUser, 
                "isGuest" : false,
                
                // env configs
                "version" : GetVersionNum(),
                "network_env" : localStorage.getItem("network_env"),
                "timezone" : GetCurrentTimezone(),
                "urlParams" : GetCachedUrlParams(),
            };
            sendMessage('UnityToWeb', 'ProcessWebConfig', JSON.stringify(playerInfo)); 
        }else{
            const playerInfo = { 
                //Player data 
                "id" : localStorage.getItem('guestID'),
                "isGuest" : true,

                // env configs
                "version" : GetVersionNum(),
                "network_env" : localStorage.getItem("network_env"),
                "timezone" : GetCurrentTimezone(),
                "urlParams" : GetCachedUrlParams(),
            };
            sendMessage('UnityToWeb', 'ProcessWebConfig', JSON.stringify(playerInfo)); 
        }   
    };

    const onUnityToggleFullscreen = (json) => {
        var value = JSON.parse(json);
        console.log(`[onUnityToggleFullscreen] unity requested to toggle fullscreen with value: ${value}`);
        requestFullscreen(value);
    };

    const getQualitySetting = () => {
        // "Low" is set via the Unity Player Settings "Graphics Quality" dropdown
        // (UnityToWeb.UpdateQuality -> onUnityUpdateQuality below). react-unity-webgl only reads
        // devicePixelRatio once, at Unity instance creation, so this preference is read from
        // localStorage here (before Unity has even loaded) rather than asked of Unity directly.
        return GetQualityPreference() === 'Low' ? Math.min(1, window.devicePixelRatio) : Math.min(2, window.devicePixelRatio);
    };

    const onUnityUpdateQuality = (json) => {
        const quality = JSON.parse(json);
        console.log(`[onUnityUpdateQuality] unity requested graphics quality: ${quality}`);
        // devicePixelRatio is baked into the Unity instance at creation and can't be changed
        // live - Unity shows its own "please refresh the page" popup on this same setting
        // change, so this just persists the value for getQualitySetting() to pick up next load.
        SetQualityPreference(quality);
    };

    const onUnityOpenStripeCheckout = (json) => {
        console.log(`[onUnityOpenStripeCheckout] unity request to stripe checkout: ${json}`);
        openStripePurchaseModal(json, onCheckoutModalComplete);
    };

    const onCheckoutModalComplete = (data) => {
        const json = JSON.stringify(data);
        console.log(`[onCheckoutModalComplete] completed checkout with result: ${json}`);
        sendMessage('UnityToWeb', 'ProcessCheckoutModalComplete', json); 
    };

    // iOS Safari only grants DeviceMotion access when requestPermission() is called
    // synchronously inside a real user gesture (tap/click) - OnUnityEnableShake below is
    // called from an async Unity->JS bridge message when a shake minigame starts, which by
    // then is well outside any gesture window, so that request silently fails on iOS
    // specifically (this permission API doesn't exist on Android at all, which is why shake
    // detection working or not has been inconsistent across phones rather than uniformly
    // broken). Piggybacking on the player's very first tap/click anywhere on the page - which
    // always happens well before they can reach a shake minigame - requests it while a real
    // gesture is still available. OnUnityEnableShake's own request becomes a no-op fallback
    // that resolves instantly once this has already run.
    useEffect(() => {
        const requestMotionPermissionOnce = async () => {
            if (motionPermissionResolvedRef.current) return;
            motionPermissionResolvedRef.current = true;
            try {
                if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
                    await DeviceMotionEvent.requestPermission();
                }
            } catch (error) {
                console.error("[requestMotionPermissionOnce] Error requesting motion permission:", error);
            }
        };
        window.addEventListener("pointerdown", requestMotionPermissionOnce, { once: true });
        return () => window.removeEventListener("pointerdown", requestMotionPermissionOnce);
    }, []);

    // This is called in app.js dunno if i also need it here
    useEffect(() => {
        let wakeLock = null;

        const requestWakeLock = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen');
                console.log('[requestWakeLock] Wake Lock activated');
            } catch (error) {
                console.warn('[requestWakeLock] Failed to activate Wake Lock:', error);
            }
        };

        // Request wake lock when component mounts
        requestWakeLock();

        // Release wake lock when component unmounts
        return () => {
            if (wakeLock !== null) {
                wakeLock.release();
                console.log('Wake Lock released');
            }
        };
    }, []);

    // Everything in this app happens inside one Unity canvas at a single "/" route - there's no
    // real in-app history for the browser's Back button to step through, so an unintercepted
    // press just navigates the player off the site entirely. This traps it instead: push one
    // buffer history entry, and on every popstate (a Back press consuming that entry) re-push a
    // fresh one immediately so the trap stays armed indefinitely, then forward the press into
    // Unity to decide what "back" means for whatever screen is currently showing. See
    // onUnityConfirmLeaveApp above for how a confirmed "actually leave" escapes this trap.
    useEffect(() => {
        if (!isLoaded) return undefined;

        window.history.pushState({ unityBackTrap: true }, '', window.location.href);

        const handlePopState = () => {
            if (exitingAppRef.current) return;
            window.history.pushState({ unityBackTrap: true }, '', window.location.href);
            console.log('[handlePopState] intercepted browser Back - forwarding to Unity');
            sendMessage('UnityToWeb', 'OnBrowserBackPressed', '');
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isLoaded, sendMessage]);

    useEffect(() => {
        console.log(`[GamePage] isLoaded has changed: ${isLoaded}`);
        if (isLoaded) {
            console.log('[GamePage] Unity has completed loading');
            if (!initializedRef.current) {
                // Ensure that initialization occurs only once
                console.log('[GamePage.UnityAttachment] Attempting to attach rmbclient to unity');
                
                // Attach Unity to Javscript Event Listeners
                addEventListener("UnityLogOut", onUnityLogOut);
                addEventListener("UnityRequestWebConfig", onUnityRequestPlayerInfo);
                addEventListener("UnityCopyToClipboard", onUnityCopyToClipboard);
                addEventListener("UnityVibrate", onUnityVibrate);
                addEventListener("UnityVibratePattern", onUnityVibratePattern);
                addEventListener("UnityToggleFullscreen", onUnityToggleFullscreen);
                addEventListener("UnityDeleteAccount", onUnityDeleteAccount);
                addEventListener("UnityUpdateCachedURLParam", onUnityUpdateCachedURLParam);
                addEventListener("UnityEnableShake", OnUnityEnableShake);
                addEventListener("UnityDisableShake", OnUnityDisableShake);
                addEventListener("UnityRefreshAuthToken", OnUnityRefreshAuthToken);
                addEventListener("OpenURL", OpenURL);
                addEventListener("UnityOpenInputField", onUnityOpenInputField);
                addEventListener("UnityCloseInputField", onUnityCloseInputField);
                addEventListener("UnityOpenPurchaseStripe", onUnityOpenStripeCheckout);
                addEventListener("UnityOpenPurchasePayPal", openPayPalPurchaseModal);
                addEventListener("UnityUpdateQuality", onUnityUpdateQuality);
                addEventListener("UnityConfirmLeaveApp", onUnityConfirmLeaveApp);
                addEventListener("UnityDownloadTextFile", onUnityDownloadTextFile);

                // Finished attaching functions
                console.log('[GamePage.UnityAttachment] Finished attaching rmbclient to unity');
                
                // Finished setup
                initializedRef.current = true;
                console.log('[GamePage.UnityAttachment] completed unity setup');
                
                // Pass playerInfo
                onUnityRequestPlayerInfo("");
            }
        }

        return () => {
            console.log(`[GamePage.UnityAttachment] exiting with ${initializedRef.current}`);
            if (initializedRef.current) {
                // Put all clean up under logic here (if needed)
                removeEventListener("UnityLogOut", onUnityLogOut);
                removeEventListener("UnityRequestPlayerInfo", onUnityRequestPlayerInfo);
                removeEventListener("UnityCopyToClipboard", onUnityCopyToClipboard);
                removeEventListener("UnityVibrate", onUnityVibrate);
                removeEventListener("UnityVibratePattern", onUnityVibratePattern);
                removeEventListener("UnityToggleFullscreen", onUnityToggleFullscreen);
                removeEventListener("UnityDeleteAccount", onUnityDeleteAccount);
                removeEventListener("UnityUpdateCachedURLParam", onUnityUpdateCachedURLParam);
                removeEventListener("UnityEnableShake", OnUnityEnableShake);
                removeEventListener("UnityDisableShake", OnUnityDisableShake);
                removeEventListener("UnityRefreshAuthToken", OnUnityRefreshAuthToken);
                removeEventListener("OpenURL", OpenURL);
                removeEventListener("UnityOpenInputField", onUnityOpenInputField);
                removeEventListener("UnityCloseInputField", onUnityCloseInputField);
                removeEventListener("UnityOpenPurchaseStripe", openStripePurchaseModal);
                removeEventListener("UnityOpenPurchasePayPal", openPayPalPurchaseModal);
                removeEventListener("UnityUpdateQuality", onUnityUpdateQuality);
                removeEventListener("UnityConfirmLeaveApp", onUnityConfirmLeaveApp);
                removeEventListener("UnityDownloadTextFile", onUnityDownloadTextFile);
                OnUnityDisableShake();
                console.log('[GamePage.UnityAttachment] Cleared all attachments from rmbclient to unity');
                initializedRef.current = false;
            }
        };
    }, [isLoaded]);

    return (
        <Fragment>
            <div className="unity-container" ref={unityContainerRef}>
                <Unity
                    unityProvider={unityProvider}
                    className="unity-content"
                    ref={unityCanvasRef}
                    style={{ visibility: isLoaded ? "visible" : "hidden" }}
                    devicePixelRatio={getQualitySetting()}
                />
            </div>

            {/* Global HTML overlay UI */}
            <ModalInputWrapper sendMessage={sendMessage} />
            <ModalPurchaseStripe />
            <ModalPurchasePayPal />

            {!isLoaded && (
                <div>
                    <div className="center center-boarder">
                        <div className="bottle-loader">
                            <div className="bubble"></div>
                            <div className="bubble"></div>
                            <div className="bubble"></div>
                        </div>
                        <br></br>
                        <p>Loading... {Math.round(loadingProgression * 100)}%</p>
                    </div>
                    <CancelButton onClick={onUnityLogOut}></CancelButton>
                </div>
            )}
        </Fragment>
    );
};
export default GamePage;